import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { t } from "ttag";

import { updatePatient } from "@/app/api/legacy-api";
import { usePatientQuery } from "@/app/hooks";
import { ROUTES } from "@/app/router";
import { triggerCustomerIoUserProfileUpdated } from "@/features/analytics/triggers";
import { queriesStore } from "@/shared/api";
import { LogoLoader } from "@/shared/components";
import { usePageTitle } from "@/shared/hooks";
import {
  COUNTRY,
  getGenderLabel,
  PUERTO_RICO_STATE_VALUE,
} from "@/shared/models";
import { patientSchema } from "@/shared/models/legacy-schemas";
import type { Patient } from "@/shared/models/legacy-types";
import {
  CalendarMinimalisticIcon,
  Card,
  ClockIcon,
  errorToast,
  FileDropzone,
  Form,
  GenderIcon,
  HomeIcon,
  Input,
  LetterIcon,
  PhoneInput,
  Select,
  UserIcon,
  useToastStore,
} from "@/shared/ui";
import {
  formatBackendDate,
  formatPhoneNumber,
  handleAxiosFieldErrors,
  parsePhoneNumber,
} from "@/shared/utils";
import { EditProfileHeader } from "./sections";

const getFormPatientValues = (data?: Patient) => {
  if (!data) {
    return {};
  }

  const { location, policy, gender, dateOfBirth, phoneNumber, ...rest } = data;

  return {
    ...rest,
    ...policy,
    state: location?.state ?? "",
    line1: location?.line1 ?? "",
    line2: location?.line2 ?? "",
    zip: location?.zip ?? "",
    city: location?.city ?? "",
    country: location?.country ?? COUNTRY.US,
    gender,
    dateOfBirth: dateOfBirth ?? "",
    phoneNumber: formatPhoneNumber(phoneNumber ?? ""),
  };
};

const getTimezoneOptions = () =>
  [
    { value: "America/New_York", label: t`Eastern Time` },
    { value: "America/Chicago", label: t`Central Time` },
    { value: "America/Denver", label: t`Mountain Time` },
    { value: "America/Los_Angeles", label: t`Pacific Time` },
    { value: "America/Phoenix", label: t`Arizona Time` },
    { value: "America/Anchorage", label: t`Alaska Time` },
    { value: "Pacific/Honolulu", label: t`Hawaii Time` },
    { value: "America/Puerto_Rico", label: t`Atlantic Time` },
  ] as const;

export const EditProfile = () => {
  usePageTitle(t`Edit profile`);

  const { pushToast } = useToastStore();
  const navigate = useNavigate();
  const { data: patient } = usePatientQuery();
  const isFromPuertoRico = patient?.location?.state === PUERTO_RICO_STATE_VALUE;

  const { data: stateOptions, isLoading: isLoadingStates } = useQuery(
    queriesStore.location.options._ctx.state(),
  );

  const form = useForm({
    resolver: zodResolver(patientSchema),
    defaultValues: { ...getFormPatientValues(patient) },
  });

  const {
    formState: { dirtyFields },
    control,
    handleSubmit,
    register,
    reset,
    setError,
    watch,
    setValue,
  } = form;

  const areValuesChanged = Object.keys(dirtyFields).length;

  const handleFileUpload = (
    file: File,
    field: "cardFrontImage" | "cardBackImage",
    idField: "cardFrontId" | "cardBackId",
  ) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result;
      if (typeof base64String === "string") {
        setValue(field, base64String, { shouldDirty: true });
        setValue(idField, null, { shouldDirty: true });
      }
    };
    reader.readAsDataURL(file);
  };

  const queryClient = useQueryClient();
  const {
    mutate: updatePatientMutation,
    isPending: isPendingUpdatePatientMutation,
  } = useMutation({
    mutationFn: updatePatient.mutation,
    onSuccess: () => {
      updatePatient.invalidates(queryClient);

      if (patient) {
        triggerCustomerIoUserProfileUpdated({ userEmail: patient.email });
      }
      void pushToast({
        type: "success",
        title: t`Success`,
        message: t`Profile successfully updated!`,
      });

      navigate(ROUTES.PROFILE.OVERVIEW);
    },
    onError: (err) => {
      errorToast(err);
      handleAxiosFieldErrors(err, setError);
    },
  });

  const isSuccess = patient && stateOptions;
  const isSaveDisabled = !areValuesChanged || isPendingUpdatePatientMutation;

  return (
    <div className="relative flex grow flex-col gap-5 px-4 pt-12 md:px-8 lg:px-16">
      <EditProfileHeader resetForm={reset} isSaveDisabled={isSaveDisabled} />

      {isSuccess && (
        <Form.Provider {...form}>
          <form
            id="edit-patient-form"
            onSubmit={handleSubmit((values) =>
              updatePatientMutation({
                ...values,
                phoneNumber: parsePhoneNumber(values.phoneNumber),
              }),
            )}
            className="flex grow flex-col gap-4 pb-10"
          >
            <Card.Root color="secondary">
              <Card.Header>
                <Card.Title>{t`General information`}</Card.Title>
              </Card.Header>
              <Card.Content className="flex grid-cols-2 flex-col gap-x-4 gap-y-4.5 sm:grid">
                <Form.Field
                  control={form.control}
                  name="firstName"
                  render={() => (
                    <Form.Item>
                      <Form.Label aria-required>{t`First name`}</Form.Label>
                      <Form.Control>
                        <Input.Root
                          {...register("firstName")}
                          placeholder={t`E.g. John`}
                        >
                          <Input.Slot>
                            <UserIcon />
                          </Input.Slot>
                        </Input.Root>
                      </Form.Control>
                      <Form.Message compact={false} />
                    </Form.Item>
                  )}
                />

                <Form.Field
                  control={form.control}
                  name="lastName"
                  render={() => (
                    <Form.Item>
                      <Form.Label aria-required>{t`Last name`}</Form.Label>
                      <Form.Control>
                        <Input.Root
                          {...register("lastName")}
                          placeholder={t`E.g. Doe`}
                        >
                          <Input.Slot>
                            <UserIcon />
                          </Input.Slot>
                        </Input.Root>
                      </Form.Control>
                      <Form.Message compact={false} />
                    </Form.Item>
                  )}
                />

                <Form.Item>
                  <Form.Label aria-required>
                    {t`Sex assigned at birth`}
                  </Form.Label>
                  <Form.Control>
                    <Input.Root readOnly value={getGenderLabel(patient.gender)}>
                      <Input.Slot>
                        <GenderIcon />
                      </Input.Slot>
                    </Input.Root>
                  </Form.Control>
                </Form.Item>

                <Form.Item>
                  <Form.Label aria-required>{t`Date of birth`}</Form.Label>
                  <Form.Control>
                    <Input.Root
                      readOnly
                      value={formatBackendDate(patient.dateOfBirth)}
                    >
                      <Input.Slot>
                        <CalendarMinimalisticIcon />
                      </Input.Slot>
                    </Input.Root>
                  </Form.Control>
                </Form.Item>
              </Card.Content>
            </Card.Root>

            <Card.Root color="secondary">
              <Card.Header>
                <Card.Title>{t`Contact`}</Card.Title>
              </Card.Header>
              <Card.Content className="flex grid-cols-2 flex-col gap-x-4 gap-y-4.5 sm:grid">
                <Form.Item>
                  <Form.Label aria-required>{t`Email`}</Form.Label>
                  <Form.Control>
                    <Input.Root readOnly value={patient.email}>
                      <Input.Slot>
                        <LetterIcon />
                      </Input.Slot>
                    </Input.Root>
                  </Form.Control>
                  <Form.Message compact={false} />
                </Form.Item>

                <Form.Field
                  control={form.control}
                  name="line1"
                  render={() => (
                    <Form.Item>
                      <Form.Label aria-required>
                        {t`House and street number`}
                      </Form.Label>
                      <Form.Control>
                        <Input.Root
                          {...register("line1")}
                          placeholder={t`123, Main Street`}
                        >
                          <Input.Slot>
                            <HomeIcon />
                          </Input.Slot>
                        </Input.Root>
                      </Form.Control>
                      <Form.Message compact={false} />
                    </Form.Item>
                  )}
                />

                <Form.Field
                  control={form.control}
                  name="line2"
                  render={() => (
                    <Form.Item>
                      <Form.Label aria-required>
                        {t`Apartment number`}
                      </Form.Label>
                      <Form.Control>
                        <Input.Root
                          {...register("line2")}
                          placeholder={t`E.g. 1A`}
                        >
                          <Input.Slot>
                            <HomeIcon />
                          </Input.Slot>
                        </Input.Root>
                      </Form.Control>
                      <Form.Message compact={false} />
                    </Form.Item>
                  )}
                />

                <Form.Field
                  control={form.control}
                  name="zip"
                  render={() => (
                    <Form.Item>
                      <Form.Label aria-required>{t`Zip code`}</Form.Label>
                      <Form.Control>
                        <Input.Root
                          {...register("zip")}
                          placeholder={t`E.g. 12345`}
                        >
                          <Input.Slot>
                            <HomeIcon />
                          </Input.Slot>
                        </Input.Root>
                      </Form.Control>
                      <Form.Message compact={false} />
                    </Form.Item>
                  )}
                />

                <Form.Field
                  control={control}
                  name="state"
                  render={({ field: { ...field } }) => (
                    <Form.Item>
                      <Form.Label aria-required>{t`State`}</Form.Label>
                      <Form.Control>
                        <Select
                          readOnly={isFromPuertoRico}
                          options={stateOptions}
                          isLoading={isLoadingStates}
                          placeholder={t`E.g. New York`}
                          leftIcon={<HomeIcon />}
                          {...field}
                        />
                      </Form.Control>
                      <Form.Message compact={false} />
                    </Form.Item>
                  )}
                />

                <Form.Field
                  control={form.control}
                  name="city"
                  render={() => (
                    <Form.Item>
                      <Form.Label aria-required>{t`City`}</Form.Label>
                      <Form.Control>
                        <Input.Root
                          {...register("city")}
                          placeholder={t`E.g. Phoenix`}
                        >
                          <Input.Slot>
                            <HomeIcon />
                          </Input.Slot>
                        </Input.Root>
                      </Form.Control>
                      <Form.Message compact={false} />
                    </Form.Item>
                  )}
                />

                <Form.Field
                  control={form.control}
                  name="phoneNumber"
                  render={() => (
                    <Form.Item>
                      <Form.Label aria-required>{t`Phone number`}</Form.Label>
                      <Form.Control>
                        <PhoneInput {...register("phoneNumber")} />
                      </Form.Control>
                      <Form.Message />
                    </Form.Item>
                  )}
                />

                <Form.Field
                  control={control}
                  name="timezone"
                  render={({ field: { ...field } }) => (
                    <Form.Item>
                      <Form.Label aria-required>{t`Time zone`}</Form.Label>
                      <Form.Control>
                        <Select
                          options={getTimezoneOptions()}
                          placeholder={t`E.g. PST`}
                          leftIcon={<ClockIcon />}
                          {...field}
                        />
                      </Form.Control>
                      <Form.Message />
                    </Form.Item>
                  )}
                />
              </Card.Content>
            </Card.Root>

            <Card.Root color="secondary">
              <Card.Header className="flex-col">
                <Card.Title>{t`Insurance`}</Card.Title>
                <Card.Description>{t`Insurance card`}</Card.Description>
              </Card.Header>
              <Card.Content className="grid gap-x-4 gap-y-4.5 sm:grid-cols-2">
                <FileDropzone
                  fileExists={Boolean(watch("cardFrontId"))}
                  id="cardFrontImage"
                  onDrop={(acceptedFiles) => {
                    const firstFile = acceptedFiles[0];
                    if (firstFile) {
                      handleFileUpload(
                        firstFile,
                        "cardFrontImage",
                        "cardFrontId",
                      );
                    }
                  }}
                  label={t`Drop your front card image here`}
                  uploadedLabel={t`Front card uploaded`}
                  value={watch("cardFrontImage")}
                />
                <FileDropzone
                  fileExists={Boolean(watch("cardBackId"))}
                  id="cardBackImage"
                  onDrop={(acceptedFiles) => {
                    const firstFile = acceptedFiles[0];
                    if (firstFile) {
                      handleFileUpload(
                        firstFile,
                        "cardBackImage",
                        "cardBackId",
                      );
                    }
                  }}
                  label={t`Drop your back card image here`}
                  uploadedLabel={t`Back card uploaded`}
                  value={watch("cardBackImage")}
                />
              </Card.Content>
            </Card.Root>
          </form>
        </Form.Provider>
      )}

      <LogoLoader
        show={isLoadingStates || isPendingUpdatePatientMutation}
        fullscreen={isPendingUpdatePatientMutation}
      />
    </div>
  );
};
